import React from 'react';
import { Navigate, Route, Routes } from 'react-router';
import Header from './layout/base/Header';
import { AboutPage } from './pages/AboutPage';
import { useResizeDetector } from 'react-resize-detector';
import { Alert, AlertVariant } from './helpers/ui';
import { StoryOverviewPage } from './pages/StoryOverviewPage';
import { WasteWaterStoryPage } from './models/wasteWater/story/WasteWaterStoryPage';
import { WasteWaterLocationPage } from './models/wasteWater/story/WasteWaterLocationPage';
import StoriesOverview from './stories/StoriesOverview';
import StoryRouter from './stories/StoryRouter';
import { useExploreUrl } from './helpers/explore-url';
import { checkSiloAvailability, fetchLapisDataVersion } from './data/api-lapis';
import { ExplorePage } from './pages/ExplorePage';
import { DeepInternationalComparisonPage } from './pages/DeepInternationalComparisonPage';
import { DeepChen2021FitnessPage } from './pages/DeepChen2021FitnessPage';
import { DeepWastewaterPage } from './pages/DeepWastewaterPage';
import { DeepSequencingCoveragePage } from './pages/DeepSequencingCoveragePage';
import { FocusPage } from './pages/FocusPage';
import { formatQcSelectorAsString, isDefaultQcSelector } from './data/QcSelector';
import { isDefaultHostSelector } from './data/HostSelector';
import { FaFilter } from 'react-icons/fa';
import { CollectionOverviewPage } from './pages/CollectionOverviewPage';
import { CollectionAddPage } from './pages/CollectionAddPage';
import { CollectionSinglePage } from './pages/CollectionSinglePage';
import {
  defaultSubmissionDateRangeSelector,
  formatDateRangeSelector,
  isDefaultSubmissionDateRangeSelector,
} from './data/DateRangeSelector';
import { useQuery } from './helpers/query-hook';
import { defaultDateRange, defaultHost, defaultSamplingStrategy } from './data/default-selectors';
import { useBaseLocation } from './helpers/use-base-location';
import Loader from './components/Loader';
import { Footer } from './layout/base/Footer';
import { RemovalOfGisaidDataPage } from './pages/news/2025-12-23-removal-of-gisaid-data';
import { DefaultDataSourceChangeBanner } from './components/banners/DefaultDataSourceChangeBanner';

const isPreview = !!process.env.REACT_APP_IS_VERCEL_DEPLOYMENT;

export const App = () => {
  const { width, ref } = useResizeDetector<HTMLDivElement>();
  const isSmallScreen = width !== undefined && width < 768;

  const { data: siloAvailability } = useQuery(checkSiloAvailability, []);
  const { data: lapisDataVersion } = useQuery(fetchLapisDataVersion, []);

  if (siloAvailability?.isAvailable === false) {
    return (
      <div className='w-full'>
        <Header hideInternalLinks />
        <div className='text-center mt-8 max-w-lg m-auto'>
          <Alert variant={AlertVariant.DANGER}>
            Our database (LAPIS) is currently unavailable. Sorry for the inconvenience!
            {siloAvailability.retryAfterInSeconds === null ? (
              <>
                {' '}
                Please try again later. You can file an issue{' '}
                <a className='underline' href='https://github.com/GenSpectrum/cov-spectrum-website/issues'>
                  on GitHub
                </a>{' '}
                if the problem persists.
              </>
            ) : (
              ` Please try again in ${Math.ceil(siloAvailability.retryAfterInSeconds / 60)} minutes.`
            )}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <DefaultDataSourceChangeBanner />
      <Header />
      <div ref={ref} className='w-full'>
        <MainContent isSmallScreen={isSmallScreen} />
      </div>
      <Footer lapisDataVersion={lapisDataVersion} />
    </div>
  );
};

type MainContentProps = {
  isSmallScreen: boolean;
};

function MainContent({ isSmallScreen }: MainContentProps) {
  const baseLocation = useBaseLocation();
  if (!baseLocation) {
    return <Loader />; // Just wait a slight bit. It should come very soon!
  }

  return (
    <>
      {isPreview && <PreviewAlert />}
      <AdvancedFiltersAlert />
      <CovSpectrumRoutes baseLocation={baseLocation} isSmallScreen={isSmallScreen} />
    </>
  );
}

function PreviewAlert() {
  return (
    <Alert variant={AlertVariant.WARNING}>
      <div className='text-center font-bold'>
        Note: This is a preview deployment. Please visit{' '}
        <a href='https://cov-spectrum.org'>https://cov-spectrum.org</a> for the official website.
      </div>
    </Alert>
  );
}

function AdvancedFiltersAlert() {
  const { host, qc, setHostAndQc, submissionDate } = useExploreUrl() ?? {};

  if (!(host && qc && submissionDate && setHostAndQc)) {
    return <></>;
  }

  let allFiltersHaveTheirDefaultValue =
    isDefaultHostSelector(host) &&
    isDefaultQcSelector(qc) &&
    isDefaultSubmissionDateRangeSelector(submissionDate);

  if (allFiltersHaveTheirDefaultValue) {
    return <></>;
  }

  return (
    <Alert variant={AlertVariant.WARNING}>
      <div className='flex flex-row'>
        <FaFilter
          className='m-1'
          style={{ width: '30px', minWidth: '30px', height: '30px', minHeight: '30px' }}
        />
        <div className='ml-4 flex-grow-1'>
          <div className='font-weight-bold'>Advanced filters are active</div>
          {!isDefaultHostSelector(host) && <div>Selected hosts: {host.join(', ')}</div>}
          {!isDefaultQcSelector(qc) && <div>Sequence quality: {formatQcSelectorAsString(qc)}</div>}
          {!isDefaultSubmissionDateRangeSelector(submissionDate) && (
            <div>Submission date: {formatDateRangeSelector(submissionDate)}</div>
          )}

          <div className='mt-4'>
            <button
              className='underline cursor-pointer'
              onClick={() => setHostAndQc(defaultHost, {}, defaultSubmissionDateRangeSelector)}
            >
              Remove filters
            </button>
          </div>
        </div>
      </div>
    </Alert>
  );
}

type CovSpectrumRoutesProps = {
  baseLocation: string;
  isSmallScreen: boolean;
};

function CovSpectrumRoutes({ baseLocation, isSmallScreen }: CovSpectrumRoutesProps) {
  return (
    <Routes>
      <Route
        path='/'
        element={
          <Navigate replace to={`/explore/${baseLocation}/${defaultSamplingStrategy}/${defaultDateRange}`} />
        }
      />
      <Route
        path='/explore/:country/:samplingStrategy/:dateRange'
        element={<ExplorePage isSmallScreen={isSmallScreen} />}
      />
      <Route
        path='/explore/:country/:samplingStrategy/:dateRange/sequencing-coverage'
        element={<DeepSequencingCoveragePage />}
      />
      <Route
        path='/explore/:country/:samplingStrategy/:dateRange/variants'
        element={<FocusPage isSmallScreen={isSmallScreen} />}
      />
      <Route
        path='/explore/:country/:samplingStrategy/:dateRange/variants/international-comparison'
        element={<DeepInternationalComparisonPage />}
      />
      <Route
        path='/explore/:country/:samplingStrategy/:dateRange/variants/waste-water'
        element={<DeepWastewaterPage />}
      />
      <Route
        path='/explore/:country/:samplingStrategy/:dateRange/variants/chen-2021-fitness'
        element={<DeepChen2021FitnessPage />}
      />
      <Route path='/story' element={<StoryOverviewPage />} />
      <Route path='/story/wastewater-in-switzerland' element={<WasteWaterStoryPage />} />
      <Route path='/stories/wastewater-in-switzerland' element={<WasteWaterStoryPage />} />
      <Route
        path='/story/wastewater-in-switzerland/location/:location'
        element={<WasteWaterLocationPage />}
      />
      <Route
        path='/stories/wastewater-in-switzerland/location/:location'
        element={<WasteWaterLocationPage />}
      />
      <Route path='/stories' element={<StoriesOverview />} />
      <Route path='/stories/:storyId' element={<StoryRouter />} />
      <Route path='/collections' element={<CollectionOverviewPage />} />
      <Route path='/collections/add' element={<CollectionAddPage />} />
      <Route path='/collections/:collectionId' element={<CollectionSinglePage />} />
      <Route path='/about' element={<AboutPage />} />
      <Route path='/news/2025-12-23-removal-of-gisaid-data' element={<RemovalOfGisaidDataPage />} />
    </Routes>
  );
}
